

import {ProjectElement} from './project.element';
import {ProjectElementType} from './project.element.type';
import {Project} from '../project';

import {Logger} from '@/util/logger';

import {basename, empty} from '@/util/util';

import {first} from 'rxjs/operators';
import {forkJoin} from 'rxjs';
import {ProjectElementFactoryDecorator} from "@/models/project/project.element.factory.decorator";

import {PropertiesHolder} from "@/models/properties.holder";
import {BlobType} from "@/models/blob";
import {TextEditorMetadata} from "@/screens/ide/text.editor.metadata";
import {JSONBrowserMetadata} from "@/screens/ide/json.browser.metadata";
import {FileBrowserMetadata} from "@/screens/ide/file.browser.metadata";

/**
 *
 * @class BatchMeasureElement
 *
 * node to maps a KPI rollup form a batch, and the existing regressions in the batch, to a
 * final summary of CSV data for the given batch in the context of this project's analysis.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2022
 *
 */
@ProjectElementFactoryDecorator('BatchMeasureElement')
export class BatchMeasureElement extends ProjectElement {

  protected actionKeys: string[] = [
    'downloadmeasurejson',
    'downloadmeasurecsv',
    'viewmeasurejson',
    'viewmeasurecsv',
    'renamemeasure',
    'deletemeasure',
    'viewinbrowser'
  ];

  /** blob key for the final measure data */
  public _jsonBlob: string = '';
  public _csvBlob: string  = '';

  /** standard constructor */
  constructor(
    project:     Project,
    id:          string,
    json:        string,
    csv:         string,
    name:        string,
    parent:      ProjectElement,
    publish?:    boolean,
    peType?:     ProjectElementType
  ) {

    super(project, id, peType ? peType : ProjectElementType.BatchMeasure);

    this._jsonBlob    = json;
    this._csvBlob     = csv;
    this._label       = name;
    this._icon        = 'fa fa-file-csv';
    this._elementKind = 'BatchMeasureElement';

    if (parent) {

      this._project.addElement(this, parent, publish);
    }

  }

  /** helper to use to for static global constructors (and factory registration) */
  static requireType() {
    console.log("requiring BatchMeasureElement");
  }

  /** drop data on this element */
  public acceptDrop(data): boolean {

    return false;
  }

  /** is this element deletable? */
  public isDeletable(): boolean {

    const kids: ProjectElement[] = this.children();

    return kids.length <= 0;
  }

  /** test if any blob of this type can be a child of this element */
  public acceptsBlobType(type: BlobType): boolean {

    return false;
  }

  /** convert this element to a simplified form that can be easily serialized */
  public node(init?: any) {

    return super.node({
      json:     this._jsonBlob,
      csv:      this._csvBlob
    });
  }

  /** initialize this element given the data in the simplified node object */
  public fromNode(node: any) {

    /* recover the basics */

    super.fromNode(node);

    /* recover the job details */

    this._jsonBlob     = node.json;
    this._csvBlob      = node.csv;
  }

  /*
   * when blobs get moved/copied in bulk, project elements have to allow being told where
   * their blobs have been moved to.
   *
   */
  public adjusted(searchKey: string, replaceKey: string) {

    if (this._csvBlob === searchKey) {

      this._csvBlob = replaceKey;
    }

    if (this._jsonBlob === searchKey) {

      this._jsonBlob = replaceKey;
    }

    super.adjusted(searchKey, replaceKey);
  }

  /** fetch any secondary blobs for this element */
  public secondaryBlobs(): string[] {

    return [this._jsonBlob, this._csvBlob];
  }

  /** do we actually have a regression? */
  public hasJSON(): boolean {

    return !empty(this._jsonBlob);
  }

  /** do we actually have a regression? */
  public hasCSV(): boolean {

    return !empty(this._csvBlob);
  }

  /**
   * subclasses must add their menu item definition(s) to the given action set,
   * in jquery-contextmenu fashion.
   *
   * @param actionSet any
   *
   */
  public actions(actionSet: any): any {

    actionSet.downloadmeasurejson = {
      name: 'Download (JSON)',
      icon: 'fas fa-file',
      disabled: !this.hasJSON()
    };

    actionSet.downloadmeasurecsv = {
      name: 'Download (CSV)',
      icon: 'fas fa-file-csv',
      disabled: !this.hasCSV()
    };

    actionSet.viewmeasurejson = {
      name: 'View (JSON)',
      icon: 'fas fa-keyboard',
      disabled: !this.hasJSON()
    };

    actionSet.viewmeasurecsv = {
      name: 'View (CSV)',
      icon: 'fas fa-keyboard',
      disabled: !this.hasCSV()
    };

    actionSet.viewinbrowser = {
      name: 'Folder Of',
      icon: 'fas fa-list',
      disabled: false
    };

    actionSet.renamemeasure = {
      name: 'Rename',
      icon: 'fas fa-tag',
      disabled: false
    };

    actionSet.deletemeasure = {
      name: 'Delete',
      icon: 'fas fa-trash',
      disabled:  false
    };

    return actionSet;
  }

  /** test if this element accepts the given action */
  public acceptsAction(key: string): boolean {

    return !!this.actionKeys.find(x => x === key);
  }

  /** append these elements properties to the holder */
  public properties(properties?: PropertiesHolder): PropertiesHolder {

    if (!properties) {

      properties = new PropertiesHolder();

    }

    properties.set('JSON Measure', this._jsonBlob);
    properties.set('CSV Measure',  this._csvBlob);

    /* add in the usual properties */

    return super.properties(properties);
  }

  /** helper to delete any cached artifacts from the remote simulation */
  public deleteResult(selection: ProjectElement[]) {

    if (this.pendingKeys.length > 0) {

      /* busy right now */

      return ;
    }

    /*
     * the cached results are internal to a "result", we just need to batch them up,
     * and remove them all with a fork/join.  In our case there are just two artifacts,
     * the log and the output.
     *
     */

    this.pendingKeys = [];

    this.pendingKeys.push([this._jsonBlob, 'latest']);
    this.pendingKeys.push([this._csvBlob, 'latest']);

    /* build out the blob deletion observables ... */

    Logger.debug('[batchmeasure] deleting blobs: ', this.pendingKeys);

    /* create the observables for copying blobs... */

    const observables: any[] = [];

    for (const f of this.pendingKeys) {

      /* as we copy we are also forcing the type to be whatever the user selected as the import type */

      observables.push( this._project.services.blobber.remove(f[0], f[1]).pipe(first()) );
    }

    /* now fork & join to do them all in parallel */

    forkJoin(observables).subscribe(
      (results) => {

        /* everything got deleted ok, add the children in the project and save the latest project */

        Logger.debug('[batchmeasure] blobs deleted: ', results);

        /* make sure the project is saved */

        this.pendingKeys = [];

        /* delete self */

        this.deleteElements([this]);

      },
      (error) => {

        Logger.error('[batchmeasure] problems deleting blobs: ', error);

      });

  }

  /** subclasses must implement their own actions */
  public action(key: string, selection: ProjectElement[]) {

    if ( !this.actionKeys.find(x => x === key) ) {

      /* let ProjectElement handle it */

      this._parent.action(key, selection);

      return ;
    }

    switch (key) {

      case 'downloadmeasurejson': {

        this.downloadJSON();

        break;
      }

      case 'downloadmeasurecsv': {

        this.downloadCSV();

        break;
      }

      case 'viewmeasurejson': {

        this._project.services.ide.launchWindow(
          new JSONBrowserMetadata(
            '',
            '',
            this._jsonBlob
          )
        );

        break;
      }

      case 'viewmeasurecsv': {

        this._project.services.ide.launchWindow(
          new TextEditorMetadata(
            '',
            '',
            this._csvBlob
          )
        );

        break;
      }

      case 'viewinbrowser': {

        this._project.services.ide.launchWindow(FileBrowserMetadata.build(this._csvBlob, this._csvBlob));

        return;
      }

      case 'renamemeasure': {

        const bme: BatchMeasureElement = this;

        bme._project.services.modals.open(
          'rename-element-modal',
          (name, resolve, reject) => {

            name = name.trim();

            Logger.debug('[BatchMeasureElement] validating: ', name);

            /* make sure we aren't name clashing ...  */

            const peers: ProjectElement[] = bme._parent._children;

            for (const peer of peers) {

              if (peer._label === name) {

                reject('Batch measure of that name already exists.');

                return ;
              }

            }

            resolve(name);

          },
          (name) => {

            /* new label was accepted */

            Logger.debug('[BatchMeasureElement] renaming: ', name);

            bme._label = `${name.trim()}`;

            bme.redraw();

            bme.save();
          },
          {
            title: 'Rename Batch Measure',
            placeholder: this._label
          });

        }
        break;

      case 'deletemeasure': {

        Logger.debug('[BatchMeasureElement] deleting');

        this.deleteElements(selection);

        break;
      }

    }
  }

  /** helper for format the regression data as a CSV...and then download that. */
  downloadJSON() {

    /* fetch the data ... */

    this._project.services.blobber.contents(this._jsonBlob, 'latest', 'text')
      .pipe(first())
      .subscribe(
        (content: any) => {

          Logger.debug(`[BatchMeasureElement] creating JSON: `, content);

          let blob: Blob   = new Blob([content], {type: 'application/json'});
          let name: string = basename(this._jsonBlob);

          const url        = window.URL.createObjectURL(blob);

          /* setup a fake one-time link with the right download name and the blob attached as the download */

          const element: HTMLAnchorElement = document.createElement('a');
          element.href          = url;
          element.download      = name;

          element.style.display = 'none';

          document.body.appendChild(element);

          /* do it */

          element.click();

          /* clean up */

          document.body.removeChild(element);

        },
        (error) => {

          Logger.error(`[BatchMeasureElement] could not fetch regression data: `, error);
        });
  }

  /** helper for format the regression data as a CSV...and then download that. */
  downloadCSV() {

    /* fetch the data ... */

    this._project.services.blobber.contents(this._csvBlob, 'latest', 'text')
      .pipe(first())
      .subscribe(
        (content: any) => {

          Logger.debug(`[BatchMeasureElement] creating CSV: `, content);

          let blob: Blob   = new Blob([content], {type: 'text/csv'});
          let name: string = basename(this._csvBlob);

          const url        = window.URL.createObjectURL(blob);

          /* setup a fake one-time link with the right download name and the blob attached as the download */

          const element: HTMLAnchorElement = document.createElement('a');
          element.href          = url;
          element.download      = name;

          element.style.display = 'none';

          document.body.appendChild(element);

          /* do it */

          element.click();

          /* clean up */

          document.body.removeChild(element);

        },
        (error) => {

          Logger.error(`[BatchMeasureElement] could not fetch regression data: `, error);
        });
  }

}
